/** The catalogue-sync page's endpoints (backend plan 2026-10-08 §3.9, P2-backend), all under
 *  `/tour_operators/{id}/catalogue/` and gated by `console_bg_sync_products`:
 *
 *  - `products/` — paged catalogue rows (`Restech_Catalogue_Product`, refreshed by the
 *    `list_products` task, never a live vendor fetch) with the resolver's effective settings,
 *    plus a `meta` block over the whole account (counts, review badge, gate, drift, coverage);
 *  - `products/{pk}/` — one product in full (resolution with sources and problems, package
 *    composition, pending change with its field diff, last run);
 *  - `registry/` — the per-product settings registry the drawer and the tenant-defaults form
 *    are GENERATED from (a new key needs no console code);
 *  - `products/{pk}/settings/` (PATCH, `null` resets), `products/bulk_settings/` (≤500),
 *    `products/{pk}/name_preview/` — settings writes and the tour-name preview;
 *  - `products/{pk}/join_preview/` — what saving `tour.join` would do (plan 2026-10-11 product
 *    joins §9): each of the product's IDs with its owner, its bookings there, every refusal;
 *  - `settings/` — the tenant's `config.catalogue.<restech>` (PATCH needs `console_top_manage`
 *    and `if_unmodified_since`; a franchise licensee forks from its parent on first write);
 *  - `runs/`, `runs/{id}/?compare=` — AI generation runs and the settings diff of two runs;
 *  - `outputs/{id}/coupling/`, `outputs/coupling/`, `outputs/{id}/recouple_preview/`;
 *  - `review/` and `products/{pk}/review/{apply,dismiss,decouple,preview}/`.
 *
 *  Shapes come from the generated spec types; never hand-mirror them. Error bodies:
 *  settings refusals are 400 `{code, detail, errors: [{code, detail, key, option_id,
 *  product_id}]}`; previews answer 409 `preview_unsupported` while the integration has no
 *  dry-run build; an apply whose sync cannot start answers 409 `{started: false, code, …}`. */
import { apiGet, apiPatch, apiPost, ConsoleApiError } from './client';
import type { components } from './types';

type S = components['schemas'];

export type CatalogueRow = S['ConsoleCatalogueRow'];
export type CatalogueAiStatus = S['ConsoleCatalogueAiStatus'];
export type CatalogueAiPlan = NonNullable<CatalogueAiStatus['plan']>;
/** A cleared `tour.join` waiting for the product's next sync to split it off the target's tour. */
export type PendingSplitOff = S['ConsoleCataloguePendingSplitOff'];
export type CatalogueRowPage = S['ConsoleCatalogueRowPage'];
export type CatalogueMeta = S['ConsoleCatalogueMeta'];
export type CatalogueGate = S['ConsoleCatalogueGate'];
export type CatalogueProduct = S['ConsoleCatalogueProductDetail'];
export type CatalogueOption = S['ConsoleCatalogueOption'];
export type CatalogueOutput = S['ConsoleCatalogueOutput'];
export type CataloguePackage = S['ConsoleCataloguePackage'];
export type CatalogueCoverageWarning = S['ConsoleCatalogueCoverageWarning'];
export type CataloguePackageIdsDrift = S['ConsoleCataloguePackageIdsDrift'];
export type CataloguePendingSummary = S['ConsoleCataloguePendingSummary'];
export type CatalogueFieldDiff = S['ConsoleCatalogueFieldDiff'];
export type CatalogueProblem = S['ConsoleCatalogueProblem'];
export type CatalogueResolution = S['ConsoleCatalogueResolution'];
export type SettingValue = S['ConsoleCatalogueSettingValue'];
export type SettingSource = SettingValue['source'];
export type SettingMap = S['ConsoleCatalogueSettingMap'];
export type SettingSpec = S['ConsoleCatalogueSettingSpec'];
export type CatalogueRegistry = S['ConsoleCatalogueRegistry'];
export type SettingError = S['ConsoleCatalogueSettingError'];
export type SettingErrors = S['ConsoleCatalogueSettingErrors'];
export type CatalogueWarning = S['ConsoleCatalogueWarning'];
export type SettingsPatch = S['ConsoleCatalogueSettingsPatch'];
export type SettingsResult = S['ConsoleCatalogueSettingsResult'];
export type BulkSettings = S['ConsoleCatalogueBulkSettings'];
export type BulkSettingsResult = S['ConsoleCatalogueBulkSettingsResult'];
/** Ops' answers for a `tour.join`: the tours its IDs may move from, and whether its bookings follow. */
export type JoinConsent = S['ConsoleCatalogueJoinConsent'];
/** What saving `tour.join` did (`join` of a settings PATCH, `joins[]` of a bulk one). */
export type JoinOutcome = S['ConsoleCatalogueJoinOutcome'];
export type JoinedId = S['ConsoleCatalogueJoinedId'];
export type JoinMove = JoinOutcome['moved'][number];
/** What saving `tour.join` would do (`join_preview/`); `ok` false = the save would refuse. */
export type JoinPreview = S['ConsoleCatalogueJoinPreview'];
export type NamePreview = S['ConsoleCatalogueNamePreview'];
export type TenantSettings = S['ConsoleCatalogueTenantSettings'];
export type TenantSettingsPatch = S['ConsoleCatalogueTenantSettingsPatch'];
export type TenantSettingsResult = S['ConsoleCatalogueTenantSettingsResult'];
export type UpstreamPolicy = NonNullable<TenantSettingsPatch['on_upstream_change']>;
export type RunSummary = S['ConsoleCatalogueRunSummary'];
export type Run = S['ConsoleCatalogueRun'];
export type RunPage = S['ConsoleCatalogueRunPage'];
export type CouplingResult = S['ConsoleCatalogueCouplingResult'];
export type CataloguePreview = S['ConsoleCataloguePreview'];
export type ReviewEntry = S['ConsoleCatalogueReviewEntry'];
export type ReviewPage = S['ConsoleCatalogueReviewPage'];
export type ReviewResult = S['ConsoleCatalogueReviewResult'];
export type ApplyResult = S['ConsoleCatalogueApplyResult'];
export type ActionError = S['ConsoleCatalogueActionError'];

/** Which restech account's catalogue (the console's sync scope). */
export interface CatalogueScope {
  tenantId: number;
  integration: string;
  /** Account slug; null = the default account. */
  account: string | null;
  /** The tenant's code, when the host knows it: shown on product pickers (tenant + pk rule). */
  tenantCode?: string | null;
}

/** Rows filters (`GET products/`); combined with AND by the backend. */
export interface CatalogueRowFilters {
  kind?: string[];
  category?: string;
  package?: boolean;
  include?: boolean;
  pending?: boolean;
  removed?: boolean;
  search?: string;
  /** `<key>:<value>` (the EFFECTIVE product setting), or `<key>` with `setting_source`. */
  setting?: string;
  setting_source?: SettingSource;
}

export interface CatalogueRowParams extends CatalogueRowFilters {
  page: number;
  page_size: number;
  ordering?: string;
}

/** Query keys — everything under `['catalogue', tenantId]`, so one invalidation after any
 *  write refreshes rows, the open product, the review queue and the badge together. */
export const catalogueKeys = {
  all: (tenantId: number) => ['catalogue', tenantId] as const,
  registry: (tenantId: number, restech: string) =>
    ['catalogue', tenantId, 'registry', restech] as const,
  rows: (scope: CatalogueScope, params: CatalogueRowParams) =>
    ['catalogue', scope.tenantId, 'rows', scope.integration, scope.account ?? '', params] as const,
  product: (tenantId: number, productId: number) =>
    ['catalogue', tenantId, 'product', productId] as const,
  tenantSettings: (tenantId: number, restech: string) =>
    ['catalogue', tenantId, 'settings', restech] as const,
  runs: (tenantId: number, params: Record<string, unknown>) =>
    ['catalogue', tenantId, 'runs', params] as const,
  run: (tenantId: number, runId: number, compare: number | null) =>
    ['catalogue', tenantId, 'run', runId, compare] as const,
  review: (scope: CatalogueScope, params: Record<string, unknown>) =>
    ['catalogue', scope.tenantId, 'review', scope.integration, scope.account ?? '', params] as const,
  /** One product of the scope looked up by its vendor id (a `catalogue_product` value). */
  productRef: (scope: CatalogueScope, vendorId: string) =>
    ['catalogue', scope.tenantId, 'ref', scope.integration, scope.account ?? '', vendorId] as const,
  joinPreview: (tenantId: number, productId: number, body: JoinPreviewBody) =>
    ['catalogue', tenantId, 'join-preview', productId, body] as const,
};

const base = (tenantId: number) => `/tour_operators/${tenantId}/catalogue`;

function scopeParams(scope: CatalogueScope): URLSearchParams {
  const usp = new URLSearchParams({ integration: scope.integration });
  if (scope.account) usp.set('account', scope.account);
  return usp;
}

/** The query string of a rows request (also used to resolve "all matching" selections). */
export function rowQuery(scope: CatalogueScope, params: Partial<CatalogueRowParams>): string {
  const usp = scopeParams(scope);
  if (params.kind?.length) usp.set('kind', params.kind.join(','));
  if (params.category) usp.set('category', params.category);
  for (const flag of ['package', 'include', 'pending', 'removed'] as const) {
    const v = params[flag];
    if (v !== undefined) usp.set(flag, String(v));
  }
  if (params.search) usp.set('search', params.search);
  if (params.setting) usp.set('setting', params.setting);
  if (params.setting_source) usp.set('setting_source', params.setting_source);
  if (params.ordering) usp.set('ordering', params.ordering);
  if (params.page) usp.set('page', String(params.page));
  if (params.page_size) usp.set('page_size', String(params.page_size));
  return usp.toString();
}

export function listRows(
  scope: CatalogueScope,
  params: Partial<CatalogueRowParams>,
  signal?: AbortSignal,
): Promise<CatalogueRowPage> {
  return apiGet(`${base(scope.tenantId)}/products/?${rowQuery(scope, params)}`, { signal });
}

export function getProduct(
  tenantId: number,
  productId: number,
  signal?: AbortSignal,
): Promise<CatalogueProduct> {
  return apiGet(`${base(tenantId)}/products/${productId}/`, { signal });
}

export function getRegistry(
  tenantId: number,
  restech: string,
  signal?: AbortSignal,
): Promise<CatalogueRegistry> {
  return apiGet(`${base(tenantId)}/registry/?integration=${encodeURIComponent(restech)}`, {
    signal,
  });
}

export function patchProductSettings(
  tenantId: number,
  productId: number,
  body: SettingsPatch,
): Promise<SettingsResult> {
  return apiPatch(`${base(tenantId)}/products/${productId}/settings/`, body);
}

export function bulkSettings(tenantId: number, body: BulkSettings): Promise<BulkSettingsResult> {
  return apiPost(`${base(tenantId)}/products/bulk_settings/`, body);
}

export function namePreview(
  tenantId: number,
  productId: number,
  body: { mode?: string | null; template?: string | null; grain?: string | null },
  signal?: AbortSignal,
): Promise<NamePreview> {
  return apiPost(`${base(tenantId)}/products/${productId}/name_preview/`, body, { signal });
}

/** `join_preview/`'s body: `target` absent or null = the product's stored join. */
export interface JoinPreviewBody {
  target?: string | null;
  join_consent?: JoinConsent;
}

export function joinPreview(
  tenantId: number,
  productId: number,
  body: JoinPreviewBody,
  signal?: AbortSignal,
): Promise<JoinPreview> {
  return apiPost(`${base(tenantId)}/products/${productId}/join_preview/`, body, { signal });
}

export function getTenantSettings(
  tenantId: number,
  restech: string,
  signal?: AbortSignal,
): Promise<TenantSettings> {
  return apiGet(`${base(tenantId)}/settings/?integration=${encodeURIComponent(restech)}`, {
    signal,
  });
}

export function patchTenantSettings(
  tenantId: number,
  restech: string,
  body: TenantSettingsPatch,
): Promise<TenantSettingsResult> {
  return apiPatch(`${base(tenantId)}/settings/?integration=${encodeURIComponent(restech)}`, body);
}

export interface RunFilters {
  product_id?: number;
  integration?: string;
  account?: string | null;
  status?: string;
  trigger?: string;
  stage?: string;
  accepted?: boolean;
  page?: number;
  page_size?: number;
}

export function listRuns(tenantId: number, f: RunFilters, signal?: AbortSignal): Promise<RunPage> {
  const usp = new URLSearchParams();
  if (f.product_id != null) usp.set('product_id', String(f.product_id));
  if (f.integration) {
    usp.set('integration', f.integration);
    // `account` narrows within an integration; the default account is `_default`.
    usp.set('account', f.account || '_default');
  }
  for (const k of ['status', 'trigger', 'stage'] as const) if (f[k]) usp.set(k, String(f[k]));
  if (f.accepted !== undefined) usp.set('accepted', String(f.accepted));
  if (f.page) usp.set('page', String(f.page));
  if (f.page_size) usp.set('page_size', String(f.page_size));
  return apiGet(`${base(tenantId)}/runs/?${usp.toString()}`, { signal });
}

export function getRun(
  tenantId: number,
  runId: number,
  compare: number | null,
  signal?: AbortSignal,
): Promise<Run> {
  const qs = compare != null ? `?compare=${compare}` : '';
  return apiGet(`${base(tenantId)}/runs/${runId}/${qs}`, { signal });
}

export function setCoupling(
  tenantId: number,
  outputId: number,
  body: { coupled: boolean; note?: string },
): Promise<CouplingResult> {
  return apiPost(`${base(tenantId)}/outputs/${outputId}/coupling/`, body);
}

export function bulkCoupling(
  tenantId: number,
  body: { output_ids: number[]; coupled: boolean; note?: string },
): Promise<CouplingResult> {
  return apiPost(`${base(tenantId)}/outputs/coupling/`, body);
}

export function recouplePreview(
  tenantId: number,
  outputId: number,
  outputIds: number[] = [],
): Promise<CataloguePreview> {
  return apiPost(`${base(tenantId)}/outputs/${outputId}/recouple_preview/`, {
    ...(outputIds.length ? { output_ids: outputIds } : {}),
  });
}

export function listReview(
  scope: CatalogueScope,
  params: { page?: number; page_size?: number },
  signal?: AbortSignal,
): Promise<ReviewPage> {
  const usp = scopeParams(scope);
  if (params.page) usp.set('page', String(params.page));
  if (params.page_size) usp.set('page_size', String(params.page_size));
  return apiGet(`${base(scope.tenantId)}/review/?${usp.toString()}`, { signal });
}

export function applyChange(tenantId: number, productId: number, note?: string): Promise<ApplyResult> {
  return apiPost(`${base(tenantId)}/products/${productId}/review/apply/`, note ? { note } : {});
}

export function dismissChange(
  tenantId: number,
  productId: number,
  note?: string,
): Promise<ReviewResult> {
  return apiPost(`${base(tenantId)}/products/${productId}/review/dismiss/`, note ? { note } : {});
}

export function decoupleInReview(
  tenantId: number,
  productId: number,
  note: string,
  outputIds?: number[],
): Promise<ReviewResult> {
  return apiPost(`${base(tenantId)}/products/${productId}/review/decouple/`, {
    note,
    ...(outputIds?.length ? { output_ids: outputIds } : {}),
  });
}

export function reviewPreview(
  tenantId: number,
  productId: number,
  outputIds?: number[],
): Promise<CataloguePreview> {
  return apiPost(
    `${base(tenantId)}/products/${productId}/review/preview/`,
    outputIds?.length ? { output_ids: outputIds } : {},
  );
}

// ---------------------------------------------------------------------------------------
// Error bodies. Classifiers return plain booleans / values (never type predicates): see the
// "negated type-predicate" lesson.
// ---------------------------------------------------------------------------------------

/** The raw JSON body of a failed catalogue call, or null for a transport failure. */
export function errorBody(e: unknown): Record<string, unknown> | null {
  if (!(e instanceof ConsoleApiError)) return null;
  const body = e.body as unknown;
  return body && typeof body === 'object' ? (body as Record<string, unknown>) : null;
}

/** The registry refusals of a 400 settings write (each with its key / option / product), or
 *  null when the error is something else (a malformed request keeps its `detail`). */
export function settingErrorsOf(e: unknown): SettingError[] | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const errors = errorBody(e)?.errors;
  if (!Array.isArray(errors)) return null;
  return errors.filter(
    (x): x is SettingError => !!x && typeof x === 'object' && typeof (x as SettingError).code === 'string',
  );
}

/** A 400 refusal of the rows filters (`invalid_filter`, `filter_too_broad`): the page keeps its
 *  filter bar so they can be changed. */
export function isFilterRefusal(e: unknown): boolean {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return false;
  const code = errorBody(e)?.code;
  return code === 'invalid_filter' || code === 'filter_too_broad';
}

/** 400 `filter_too_broad` (`param: setting`): `?setting=` is resolved product by product, and
 *  the other filters leave more products than the backend resolves in one request. */
export function isFilterTooBroad(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 400 && errorBody(e)?.code === 'filter_too_broad';
}

/** 409 `preview_unsupported`: the integration has no dry-run build yet. */
export function isPreviewUnsupported(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 409 && errorBody(e)?.code === 'preview_unsupported';
}

/** 409 `config_modified`: the tenant config changed after the form loaded it. */
export function isConfigModified(e: unknown): boolean {
  return e instanceof ConsoleApiError && e.status === 409 && errorBody(e)?.code === 'config_modified';
}

/** An apply whose sync could not start (409 `{started: false, code, detail, …}`): the change
 *  stays pending. Null for any other error (`not_pending` included). */
export function applyRefusalOf(e: unknown): ApplyResult | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 409) return null;
  const body = errorBody(e);
  return body && body.started === false ? (body as unknown as ApplyResult) : null;
}

/** The `{path: message}` map of a 400 tenant-settings PATCH (`invalid_request`). */
export function fieldErrorsOf(e: unknown): Record<string, string> | null {
  if (!(e instanceof ConsoleApiError) || e.status !== 400) return null;
  const errors = errorBody(e)?.errors;
  if (!errors || typeof errors !== 'object' || Array.isArray(errors)) return null;
  return errors as Record<string, string>;
}
